<script lang="ts">
  import type { Contributions, ProcessVideo } from "$types";
  import Galleryheading from "$components/dashboard/gallery/Galleryheading.svelte";
  import EpisodeContent from "$lib/components/episodes/EpisodeContent.svelte";
  import MapBox from "$lib/components/episodes/Map.svelte";
  import Gallery from "$lib/components/episodes/ContributionGallery.svelte";
  import PaginationBar from "./PaginationBar.svelte";
  import CurrentlyViewing from "./CurrentlyViewing.svelte";
  import { tick } from "svelte";
  import type { PageData } from "./$types";
  import {
    active_page,
    page_size,
    is_search_mode_on,
    results,
    active_filter,
    filters,
  } from "$components/episodes/gallery_store";
  import { onDestroy, onMount } from "svelte";
  import "$lib/styles/forms.css";
  import Buttons from "./Buttons.svelte";
  import Parameters from "$components/episodes/Parameters.svelte";
  import { browser } from "$app/environment";
  import { MagnifyingGlass } from "phosphor-svelte";

  export let data: PageData;

  const markers: [number, number][] = data.markers as [number, number][];
  let loaded: boolean = false;
  let is_last: boolean = false;
  let has_run: boolean = false;
  let scroll_return: HTMLElement;
  let laster: HTMLElement;

  // sets up a clamp to have nice looking markup, lowest = 1, highest = length
  const clamp = (number: number) =>
    Math.max(1, Math.min(number, data.totalGallery.length));

  $: number_of_pages = data.is_bot
    ? 1
    : Math.ceil(
        (($is_search_mode_on ? $results.length : data.totalGallery.length) ??
          0) / $page_size,
      );
  $: guest_buffer =
    $active_page === 1
      ? data.hostBuffer + data.guestBuffer
      : $page_size * ($active_page - 1);
  $: guest_gallery = data.totalGallery.slice(
    guest_buffer,
    clamp($page_size * $active_page),
  );

  // we want to show the whole gallery to crawlers..
  if (data.is_bot && !browser) {
    $active_page = 1;
    $page_size = data.totalGallery.length;
  }

  onMount(async () => {
    loaded = true;

    // reset filter
    activate_filter(0);

    // this will trigger the absolute last page
    const url = new URL(window.location.toString());
    if (url.searchParams.get("q") === "last" && !data.is_bot) {
      is_last = true;
      $page_size = 10;
      $active_page = Math.ceil(data.totalGallery.length / 10);
    } else {
      $active_page = 1;
    }

    if (is_last && !has_run) {
      const top: number = laster.getBoundingClientRect().top - 100;

      // this still only kind of works
      await tick();
      window.scrollTo({
        top,
        behavior: "smooth",
      });

      // get rid of the url param hehe
      url.searchParams.delete("q");
      history.replaceState(history.state, "", url);
    }
    has_run = true;
  });

  onDestroy(() => {
    loaded = false;
    $active_page = 1;
  });

  // add an identifier to the host contributions
  let host_contributions = data.episode.host_contributions?.map((c) => {
    return {
      ...c,
      host: true,
    };
  });

  // setup the made live gallery
  let made_live: Contributions[] = [];
  if (host_contributions) {
    made_live = [...host_contributions];
  }
  if (
    data.episode.guest_contributions &&
    data.episode.guest_contributions.length > 0
  ) {
    made_live = [...made_live, ...data.episode.guest_contributions];
  }

  const activate_filter = (index: number) => {
    $active_filter = index;
    if ($active_filter === 0) {
      $is_search_mode_on = false;
    } else {
      $is_search_mode_on = true;
      $results = data.totalGallery.filter((c) => {
        if ($active_filter === 1) {
          return c.member;
        } else if ($active_filter === 2) {
          return c.extra_image_1;
        } else if ($active_filter === 3) {
          if (!data.episode?.process_videos) return false;
          return data?.episode?.process_videos.find(
            (video: ProcessVideo) => video.member?.id === c.member,
          );
        }
      });
      gallery_title = filters[$active_filter];
    }
  };

  let show_search_bar = false;
  let query: string = "";
  let search_input: HTMLInputElement;

  const toggle_search = async () => {
    show_search_bar = !show_search_bar;
    $active_filter = 0;
    $is_search_mode_on = false;
    query = "";
    has_searched = false;

    await tick();
    if (show_search_bar && search_input) {
      search_input.focus();
    }
  };

  let has_searched: boolean = false;

  let last_search = "";
  const search = () => {
    has_searched = true;
    if (query === last_search) return;
    last_search = query;
    const term = query.toLowerCase();
    const keys_to_search = [
      "name",
      "description",
      "desc_project",
      "desc_colors",
      "desc_element",
      "desc_random",
    ];

    $results = data.totalGallery.filter((item) =>
      keys_to_search.some((key) => {
        const field = item[key]; // Get the field value
        return typeof field === "string" && field.toLowerCase().includes(term); // Check if field is a string and includes the search term
      }),
    );

    if ($results.length > 0) {
      $active_page = 1;
      $is_search_mode_on = true;
      gallery_title = "Search Results";
    } else {
      gallery_title = "No Matches Found";
    }
  };

  let gallery_title: string = "Made By Our Community";

  // Function to find and filter duplicates based on name and email in data.totalGallery
  // Function to find and filter all duplicates based on name and email in data.totalGallery
  const find_duplicates = () => {
    const occurrenceMap = new Map<string, number>();

    // Count occurrences of each name-email combination
    data.totalGallery.forEach((c) => {
      const key = `${c.name}-${c.email}`;
      occurrenceMap.set(key, (occurrenceMap.get(key) ?? 0) + 1);
    });

    // Filter contributions that have more than one occurrence
    const duplicates = data.totalGallery.filter((c) => {
      const key = `${c.name}-${c.email}`;
      return (occurrenceMap.get(key) ?? 0) > 1;
    });

    // Sort the duplicates array by `name` and `email` to group them together
    duplicates.sort((a, b) => {
      const aKey = `${a.name}-${a.email}`;
      const bKey = `${b.name}-${b.email}`;
      return aKey.localeCompare(bKey);
    });

    return duplicates;
  };

  let showing_duplicates: boolean = false;
  const show_duplicates = () => {
    showing_duplicates = !showing_duplicates;
    if (showing_duplicates) {
      $results = find_duplicates();
      $active_page = 1;
      $is_search_mode_on = true;
      gallery_title = "Duplicates";
    } else {
      $results = [];
      $active_page = 1;
      $is_search_mode_on = false;
      gallery_title = "Made By Our Community";
    }
  };

  let show_admin_link: boolean = false;
</script>

<EpisodeContent>
  <svelte:fragment slot="map">
    <MapBox {markers} />
  </svelte:fragment>
  <svelte:fragment slot="below_content">
    {#if data.episode.project && data.episode.colors && data.episode.element && data.episode.random}
      <Parameters
        project={data.episode.project}
        colors={data.episode.colors}
        element={data.episode.element}
        random={data.episode.random}
      />
    {/if}
    {#if data.totalGallery.length <= 0}
      <p style:padding-top="2rem">
        No contributions have been submitted for this episode yet.
      </p>
    {/if}
    <PaginationBar
      length={$is_search_mode_on ? $results.length : data.totalGallery.length}
    />
    <div class="under">
      <div class="tools">
        {#if show_search_bar}
          <div class="search">
            <form on:submit|preventDefault={search}>
              <input
                type="text"
                bind:value={query}
                bind:this={search_input}
                placeholder="Search"
              />
              <button disabled={query.length === 0} type="submit"
                ><MagnifyingGlass size="1.3rem" /></button
              >
            </form>
          </div>
          {#if has_searched}
            <div class="results">
              {#if $results.length === 0}
                <p>No matches found for "{last_search}"</p>
              {:else}
                <p>
                  {$results.length}
                  {$results.length === 1 ? "result" : "results"}
                  for "{query}"
                </p>
              {/if}
            </div>
          {/if}
        {:else}
          <div class="filters">
            {#each filters as filter, index}
              <button
                on:click={() => activate_filter(index)}
                class:active={index === $active_filter}>{filter}</button
              >
            {/each}
          </div>
        {/if}
        <button
          class="search_mode"
          class:active={show_search_bar}
          on:click={toggle_search}
        >
          {#if show_search_bar}
            Close Search
          {:else}
            Search Mode
          {/if}
        </button>
      </div>

      {#if data.is_admin}
        <div class="admin_tools">
          <div class="title">Admin Tools:</div>
          <div class="buttons">
            <button on:click={show_duplicates} class:active={showing_duplicates}
              >highlight duplicates</button
            >
            <button
              on:click={() => (show_admin_link = !show_admin_link)}
              class:active={show_admin_link || showing_duplicates}
              >show admin links</button
            >
          </div>
        </div>
      {/if}
      <CurrentlyViewing
        length={$is_search_mode_on ? $results.length : data.totalGallery.length}
      />
    </div>
    {#if data.totalGallery.length > 0}
      <div bind:this={scroll_return} class="hider" />
      {#if $is_search_mode_on}
        {#key $active_filter || $results}
          <Galleryheading heading={gallery_title} />
          <Gallery
            gallery={$results}
            context="public"
            base="/episodes/episode-{data.episodeNumber}/gallery"
            admin_mode={showing_duplicates || show_admin_link}
          />
        {/key}
      {:else}
        {#if $active_page === 1}
          {#if (data.episode.host_contributions && data.episode.host_contributions.length > 0) || (data.episode.guest_contributions && data.episode.guest_contributions.length > 0)}
            <Galleryheading heading="Made Live On The Show" />
            <Gallery
              gallery={made_live}
              context="live"
              base="/episodes/episode-{data.episodeNumber}/gallery"
              admin_mode={show_admin_link}
            />
          {/if}
        {/if}

        {#if guest_gallery.length > 0}
          <Galleryheading heading="Made By Our Community" />
          {#key $active_page}
            <Gallery
              gallery={guest_gallery}
              context="public"
              base="/episodes/episode-{data.episodeNumber}/gallery"
              admin_mode={show_admin_link}
            />
          {/key}
        {/if}

        <div class="laster" bind:this={laster} />
        {#if number_of_pages > 1}
          <div class="end">
            <CurrentlyViewing
              length={$is_search_mode_on
                ? $results.length
                : data.totalGallery.length}
            />
            <Buttons
              length={$is_search_mode_on
                ? $results.length
                : data.totalGallery.length}
            />
          </div>
        {/if}
      {/if}
    {/if}
  </svelte:fragment>
</EpisodeContent>

<slot />

<style>
  .search {
    display: flex;
    gap: 0.7rem;
    align-items: center;
  }
  .search form {
    position: relative;
  }
  .search input {
    width: 100%;
  }
  .search button {
    background: transparent;
    color: var(--offDark);
    position: absolute;
    width: 3rem;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    right: 0;
    border-radius: 0.3rem;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
  }
  .search button:disabled {
    color: var(--comment_accent);
  }

  .search_mode {
    background: white;
    color: var(--title);
    padding: 0.3rem 0.5rem;
    border-radius: 0.3rem;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--comment_accent);
  }

  .search_mode.active {
    border: 1px solid var(--title);
    background: var(--title);
    color: white;
  }
  .tools {
    border-bottom: 1px solid var(--dots);
    padding-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  .admin_tools {
    display: flex;
    gap: 0.7rem;
    align-items: center;
    flex-wrap: wrap;
  }
  .tools .filters {
    display: flex;
    gap: 0.7rem;
  }

  .admin_tools .title {
    font-size: 0.9rem;
    font-weight: bold;
    color: var(--offDark);
  }

  .tools .filters button,
  .admin_tools button {
    background: white;
    color: var(--offDark);
    padding: 0.3rem 0.5rem;
    border-radius: 0.3rem;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--comment_accent);
  }

  .tools .filters button.active,
  .admin_tools button.active {
    border: 1px solid var(--title);
    background: var(--title);
    color: white;
  }

  @media (max-width: 600px) {
    .tools {
      flex-direction: column;
      gap: 1rem;
      flex-wrap: wrap;
    }
    .tools .filters {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }
  .end {
    background: white;
    padding: 2rem;
    box-shadow: var(--shadow);
    border-radius: 2rem 2rem;
  }

  .under {
    display: flex;
    flex-flow: column;
    gap: 1rem;
    background: white;
    padding: 2rem;
    box-shadow: var(--shadow);
    border-radius: 0 0 2rem 2rem;
  }
  @media (max-width: 1024px) {
    .under {
      padding: 1rem;
      border-radius: 0;
    }
  }

  .laster {
    height: 1px;
  }
</style>
